import { memo, useMemo, useState } from 'react'
import { Box, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from 'react-i18next'
import { FA_COLOR, FA_ORDER, type EmotionKey } from '@/components/researcher/liveDemo/constants'
import type { EmotionSeries } from '@/services/emotionAnalysisService'
import { computeEmotionStats, computeMomentStats, reactivityBand, diversityBand, DIVERSITY_MAX } from './emotionStats'

const pct = (v: number) => `${Math.round(v * 100)}%`

type StatsMode = 'moment' | 'average'

/**
 * Stats card sitting between the filters and the respondents list. Distils the
 * active emotion series (aggregate of the filtered set, or a single respondent's
 * curve) into a distribution donut plus a reactivity / diversity read-out — so a
 * researcher can see *what* a stimulus evoked and *how strongly* at a glance.
 *
 * Two modes via the header toggle: "moment" follows the video playhead and shows
 * the instantaneous distribution at `currentMs`; "average" time-averages the whole
 * clip (the reactivity / diversity tiles are average-only — they don't apply to a
 * single frame).
 */
function EmotionStatsPanel({
  series,
  gridMs,
  currentMs,
}: {
  series: EmotionSeries | null | undefined
  gridMs: number[]
  currentMs: number
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('emotions')
  const { t: tEmo } = useTranslation('liveDemo')

  const [mode, setMode] = useState<StatsMode>('moment')

  const stats = useMemo(() => computeEmotionStats(series), [series])
  const moment = useMemo(() => computeMomentStats(series, gridMs, currentMs), [series, gridMs, currentMs])

  const isMoment = mode === 'moment'
  // Active distribution + reactivity driving the donut/legend for the chosen mode.
  const dist = isMoment ? moment.avg : stats.avg
  const reactivity = isMoment ? moment.reactivity : stats.reactivity

  const band = reactivityBand(reactivity)
  const bandColor = band === 'high' ? c.success : band === 'medium' ? c.coral : c.ink48
  const divBand = diversityBand(stats.diversity)

  // Emotions ordered by share for the legend (only the present ones), for the active mode.
  const ranked = useMemo(
    () => FA_ORDER.map((k) => ({ k, v: dist[k] })).filter((e) => e.v > 0.005).sort((a, b) => b.v - a.v),
    [dist],
  )

  return (
    <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3 }}>
      <Stack direction="row" spacing={0.85} alignItems="center">
        <Box sx={{ color: c.coral, display: 'grid', placeItems: 'center' }}>
          <InsightsRoundedIcon fontSize="small" />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 14 }}>{t('stats.title')}</Typography>
        <Box sx={{ flex: 1 }} />
        {stats.hasData && (
          <ModeToggle
            mode={mode}
            onChange={setMode}
            labels={{ moment: t('stats.mode.moment'), average: t('stats.mode.average') }}
          />
        )}
      </Stack>

      {!stats.hasData ? (
        <Typography sx={{ color: c.ink56, fontSize: 13, mt: 1.25 }}>{t('stats.noData')}</Typography>
      ) : isMoment && !moment.hasData ? (
        <Typography sx={{ color: c.ink56, fontSize: 13, mt: 1.25 }}>{t('stats.noMomentData')}</Typography>
      ) : (
        <>
          {/* Distribution donut + ranked legend */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.5 }}>
            <DistributionDonut avg={dist} reactivity={reactivity} reactivityColor={bandColor} label={t('stats.reactivity')} />
            <Stack spacing={0.65} sx={{ flex: 1, minWidth: 0 }}>
              {ranked.map(({ k, v }) => (
                <Stack key={k} direction="row" alignItems="center" spacing={0.85}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '3px', background: FA_COLOR[k], flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12.5, color: c.ink72, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {tEmo(`emotions.${k}`)}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: c.ink, fontVariantNumeric: 'tabular-nums' }}>
                    {pct(v)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>

          {/* Reactivity + diversity tiles — average-only; they describe the whole
              clip and have no meaning for a single playhead frame. */}
          {!isMoment && (
          <Stack direction="row" spacing={1.25} sx={{ mt: 1.75 }}>
            <StatTile
              label={
                <Stack direction="row" spacing={0.4} alignItems="center">
                  <span>{t('stats.reactivity')}</span>
                  <Tooltip title={t('stats.reactivityHint')} arrow placement="top">
                    <InfoOutlinedIcon sx={{ fontSize: 13, color: c.ink48 }} />
                  </Tooltip>
                </Stack>
              }
              value={pct(stats.reactivity)}
              accent={bandColor}
              footer={
                <Stack spacing={0.5}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: bandColor }}>
                    {t(`stats.band.${band}`)}
                  </Typography>
                  <Box sx={{ height: 4, borderRadius: 34, bgcolor: c.gray100, overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: pct(stats.reactivity), bgcolor: bandColor }} />
                  </Box>
                  <Typography sx={{ fontSize: 10.5, color: c.ink48 }}>
                    {t('stats.peak', { value: pct(stats.peakReactivity) })}
                  </Typography>
                </Stack>
              }
            />
            <StatTile
              label={
                <Stack direction="row" spacing={0.4} alignItems="center">
                  <span>{t('stats.diversity')}</span>
                  <Tooltip title={t('stats.diversityHint')} arrow placement="top">
                    <InfoOutlinedIcon sx={{ fontSize: 13, color: c.ink48 }} />
                  </Tooltip>
                </Stack>
              }
              value={`${stats.diversity}`}
              valueSuffix={`/ ${DIVERSITY_MAX}`}
              accent={c.ink}
              footer={
                <Stack spacing={0.5}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: c.ink72 }}>
                    {t(`stats.diversityBand.${divBand}`)}
                  </Typography>
                  {/* One segment per non-neutral emotion slot; filled ones are
                      coloured by the emotion that crossed the 5% threshold. */}
                  <Stack direction="row" spacing={0.5}>
                    {Array.from({ length: DIVERSITY_MAX }).map((_, i) => (
                      <Box
                        key={i}
                        sx={{
                          flex: 1,
                          height: 4,
                          borderRadius: 34,
                          bgcolor: i < stats.present.length ? FA_COLOR[stats.present[i]] : c.gray100,
                        }}
                      />
                    ))}
                  </Stack>
                  <Typography sx={{ fontSize: 10.5, color: c.ink48, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {t('stats.dominant', { emotion: tEmo(`emotions.${stats.dominant}`) })}
                  </Typography>
                </Stack>
              }
            />
          </Stack>
          )}
        </>
      )}
    </Paper>
  )
}

// ───────────────────────── Mode toggle ─────────────────────────

function ModeToggle({
  mode,
  onChange,
  labels,
}: {
  mode: StatsMode
  onChange: (m: StatsMode) => void
  labels: Record<StatsMode, string>
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Box sx={{ display: 'inline-flex', p: '2px', borderRadius: 999, bgcolor: c.gray100 }}>
      {(['moment', 'average'] as const).map((m) => {
        const active = mode === m
        return (
          <Box
            key={m}
            role="button"
            onClick={() => onChange(m)}
            sx={{
              px: 1.1,
              py: 0.35,
              borderRadius: 999,
              cursor: 'pointer',
              userSelect: 'none',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1.2,
              color: active ? c.ink : c.ink56,
              bgcolor: active ? '#fff' : 'transparent',
              boxShadow: active ? '0 1px 2px rgba(0,0,0,0.10)' : 'none',
              transition: 'background-color .15s, color .15s, box-shadow .15s',
            }}
          >
            {labels[m]}
          </Box>
        )
      })}
    </Box>
  )
}

// ───────────────────────── Donut ─────────────────────────

function DistributionDonut({
  avg,
  reactivity,
  reactivityColor,
  label,
}: {
  avg: Record<EmotionKey, number>
  reactivity: number
  reactivityColor: string
  label: string
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const SIZE = 132, R = 50, SW = 14, CX = SIZE / 2
  const CIRC = 2 * Math.PI * R

  // Cumulative arc offsets, in display order so colours read consistently.
  let offset = 0
  const segments = FA_ORDER.map((k) => {
    const frac = Math.max(0, avg[k])
    const len = frac * CIRC
    const seg = { k, len, offset }
    offset += len
    return seg
  }).filter((s) => s.len > 0.5)

  return (
    <Box sx={{ position: 'relative', width: SIZE, height: SIZE, flexShrink: 0 }}>
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
        <circle cx={CX} cy={CX} r={R} fill="none" stroke={c.gray100} strokeWidth={SW} />
        <g transform={`rotate(-90 ${CX} ${CX})`}>
          {segments.map((s) => (
            <circle
              key={s.k}
              cx={CX}
              cy={CX}
              r={R}
              fill="none"
              stroke={FA_COLOR[s.k]}
              strokeWidth={SW}
              strokeDasharray={`${s.len} ${CIRC - s.len}`}
              strokeDashoffset={-s.offset}
            />
          ))}
        </g>
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center' }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1, color: reactivityColor, fontVariantNumeric: 'tabular-nums' }}>
            {pct(reactivity)}
          </Typography>
          <Typography sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: c.ink48, mt: 0.4 }}>
            {label}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

// ───────────────────────── Stat tile ─────────────────────────

function StatTile({
  label,
  value,
  valueSuffix,
  accent,
  footer,
}: {
  label: React.ReactNode
  value: string
  valueSuffix?: string
  accent: string
  footer?: React.ReactNode
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Box sx={{ flex: 1, minWidth: 0, p: 1.25, borderRadius: 2, bgcolor: c.gray50, border: `1px solid ${c.ink08}` }}>
      <Typography component="div" sx={{ fontSize: 11, fontWeight: 700, color: c.ink56 }}>
        {label}
      </Typography>
      <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mt: 0.25 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1, color: accent, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Typography>
        {valueSuffix && <Typography sx={{ fontSize: 12, fontWeight: 600, color: c.ink48 }}>{valueSuffix}</Typography>}
      </Stack>
      {footer && <Box sx={{ mt: 0.75 }}>{footer}</Box>}
    </Box>
  )
}

export default memo(EmotionStatsPanel)
